import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, DatePicker, InputNumber, Row, Col, Card, AutoComplete, message, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { useStyles } from '../../theme/hooks';
import { getLoanTypes } from '../../services/loanService';
import { UserAddOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import CustomerForm from '../CustomerModal/CustomerForm';
import { searchCustomer } from '../../services/customerService';
import { createLoan, updateLoan } from '../../services/loanService';

const { Option } = Select;

const useLoanFormStyles = () => useStyles((theme) => ({
  form: {
    maxWidth: '100%',
    padding: theme.spacing.lg
  },
  section: {
    marginBottom: theme.spacing.xl,
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.sm
  },
  sectionTitle: {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.medium,
    marginBottom: theme.spacing.md,
    color: theme.colors.text.primary
  },
  buttons: {
    marginTop: theme.spacing.xl,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing.md
  }
}));

const LoanForm = ({ 
  initialValues = null, 
  mode = 'create',
  onSuccess,
  onCancel,
  loading: externalLoading
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isCustomerModalVisible, setIsCustomerModalVisible] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loanTypes, setLoanTypes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [filteredBanks, setFilteredBanks] = useState([]);
  const [poolers, setPoolers] = useState([]);
  const [referredBy, setReferredBy] = useState([]);
  const styles = useLoanFormStyles();

  useEffect(() => {
    const fetchLoanTypes = async () => {
      try {
        const types = await getLoanTypes();
        setLoanTypes(types);
      } catch (error) {
        console.error('Error fetching loan types:', error);
      }
    };

    fetchLoanTypes();
  }, []);

  useEffect(() => {
    const loadFormData = async () => {
      try {
        // Load loan types, banks, poolers, etc.
        // TODO: Implement these service calls
      } catch (error) {
        console.error('Error loading form data:', error);
        message.error(t('loanForm.errors.loadFailed'));
      }
    };

    loadFormData();
  }, [t]);

  useEffect(() => {
    if (mode === 'edit' && initialValues) {
      const formattedValues = {
        ...initialValues,
        customerDisplay: initialValues.customer ? 
          `${initialValues.customer.name}${initialValues.customer.first_name ? ` ${initialValues.customer.first_name}` : ''} - ${initialValues.customer.city || ''}` : '',
        reportedAt: initialValues.reportedAt ? dayjs(initialValues.reportedAt) : null,
        signatureDate: initialValues.signatureDate ? dayjs(initialValues.signatureDate) : null,
        statementReceivedDate: initialValues.statement_received_date ? dayjs(initialValues.statement_received_date) : null,
        invoiceDate: initialValues.invoice_date ? dayjs(initialValues.invoice_date) : null,
        consultantBilledDate: initialValues.consultant_billed_date ? dayjs(initialValues.consultant_billed_date) : null,
        temporaryBilledDate: initialValues.temporary_billed_date ? dayjs(initialValues.temporary_billed_date) : null,
      };
      form.setFieldsValue(formattedValues);
      setSelectedCustomer(initialValues.customer);
    }
  }, [mode, initialValues, form]);

  const handleCustomerSearch = async (value) => {
    if (!value) {
      setCustomerOptions([]);
      return;
    }

    try {
      const response = await searchCustomer(value);
      const options = response.map(customer => ({
        value: `${customer.name}${customer.first_name ? ` ${customer.first_name}` : ''} - ${customer.city || ''}`,
        label: `${customer.name}${customer.first_name ? ` ${customer.first_name}` : ''} - ${customer.city || ''}`,
        data: customer,
        key: customer.id
      }));
      
      setCustomerOptions(options);
    } catch (error) {
      console.error('Error searching customers:', error);
      message.error(t('loanForm.errors.customer.searchError'));
    }
  };

  const handleCustomerSelect = (value, option) => {
    setSelectedCustomer(option?.data || null);
    form.setFieldsValue({ 
      customer: option?.data?.id,
      customerDisplay: value 
    });
  };

  const handleProvisionChange = (value) => {
    if (value === null || value === undefined) {
      form.setFieldValue('provision', null);
      return;
    }
    const stringValue = value.toString();
    const apiValue = stringValue.replace(',', '.');
    form.setFieldValue('provision', apiValue);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const formattedValues = {
        ...values,
        customer: values.customer,
        reportedAt: values.reportedAt?.format('YYYY-MM-DD'),
        signatureDate: values.signatureDate?.format('YYYY-MM-DD'),
        statement_received_date: values.statementReceivedDate?.format('YYYY-MM-DD'),
        invoice_date: values.invoiceDate?.format('YYYY-MM-DD'),
        consultant_billed_date: values.consultantBilledDate?.format('YYYY-MM-DD'),
        temporary_billed_date: values.temporaryBilledDate?.format('YYYY-MM-DD'),
      };

      const response = mode === 'create' 
        ? await createLoan(formattedValues)
        : await updateLoan(initialValues.id, formattedValues);

      message.success(t(`loanForm.messages.${mode}Success`));
      if (onSuccess) {
        onSuccess(response);
      }
    } catch (error) {
      console.error('Error saving loan:', error);
      message.error(t(`loanForm.messages.${mode}Error`));
    } finally {
      setLoading(false);
    }
  };

  const renderStatusDependentFields = () => {
    if (mode !== 'edit' || !initialValues?.status) return null;

    const status = initialValues.status;
    
    return (
      <>
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>{t('loan.details.payment')}</h3>
          <Row gutter={24}>
            {/* Felder für PAYMENT_CONFIRMED und höher */}
            {['PAYMENT_CONFIRMED', 'PAID', 'CONSULTANT_BILLED'].includes(status) && (
              <>
                <Col span={12}>
                  <Form.Item
                    name="statementReceivedDate"
                    label={t('loan.fields.statementReceivedDate')}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="statementReceivedNumber"
                    label={t('loan.fields.statementReceivedNumber')}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </>
            )}

            {/* Felder für PAID und höher */}
            {['PAID', 'CONSULTANT_BILLED'].includes(status) && (
              <>
                <Col span={12}>
                  <Form.Item
                    name="invoiceDate"
                    label={t('loan.fields.invoiceDate')}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="invoiceNumber"
                    label={t('loan.fields.invoiceNumber')}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="invoiceComment"
                    label={t('loan.fields.invoiceComment')}
                  >
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </Col>
              </>
            )}

            {/* Felder für CONSULTANT_BILLED */}
            {status === 'CONSULTANT_BILLED' && (
              <>
                <Col span={12}>
                  <Form.Item
                    name="consultantBilledDate"
                    label={t('loan.fields.consultantBilledDate')}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="consultantBilledNumber"
                    label={t('loan.fields.consultantBilledNumber')}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                {initialValues.temporary_consultant && (
                  <>
                    <Col span={12}>
                      <Form.Item
                        name="temporaryBilledDate"
                        label={t('loan.fields.temporaryBilledDate')}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="temporaryBilledNumber"
                        label={t('loan.fields.temporaryBilledNumber')}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </>
                )}
              </>
            )}
          </Row>
        </div>
      </>
    );
  };

  return (
    <Card bordered={false} style={{ background: 'white' }}>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          isDirectSubmission: true,
          isPrognosis: true,
          reportedAt: dayjs()
        }}
        style={styles.form}
      >
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>{t('loan.basicInfo')}</h3>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="amount"
                label={t('loan.amount')}
                rules={[{ required: true, message: t('validation.required') }]}
              >
                <InputNumber
                  min={0}
                  formatter={value => `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/€\s?|(,*)/g, '')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="loanType"
                label={t('loan.type')}
                rules={[{ required: true, message: t('validation.required') }]}
              >
                <Select>
                  {loanTypes.map(type => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="interestRate"
                label={t('loan.interestRate')}
                rules={[{ required: true, message: t('validation.required') }]}
              >
                <InputNumber
                  min={0}
                  max={100}
                  formatter={value => `${value}%`}
                  parser={value => value.replace('%', '')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="term"
                label={t('loan.term')}
                rules={[{ required: true, message: t('validation.required') }]}
              >
                <InputNumber
                  min={1}
                  formatter={value => `${value} ${t('loan.months')}`}
                  parser={value => value.replace(t('loan.months'), '')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>{t('loan.additionalInfo')}</h3>
          <Form.Item
            name="purpose"
            label={t('loan.purpose')}
            rules={[{ required: true, message: t('validation.required') }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="collateral"
            label={t('loan.collateral')}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </div>

        {renderStatusDependentFields()}

        <div style={styles.buttons}>
          {onCancel && (
            <Button 
              onClick={onCancel}
              style={{ marginRight: 16 }}
            >
              {t('common.cancel')}
            </Button>
          )}
          <Button 
            type="primary" 
            onClick={handleSubmit}
            loading={loading || externalLoading}
          >
            {t(`loanForm.${mode}.submit`)}
          </Button>
        </div>
      </Form>

      <CustomerForm 
        visible={isCustomerModalVisible}
        onCancel={() => setIsCustomerModalVisible(false)}
        onSuccess={(customer) => {
          setIsCustomerModalVisible(false);
          const customerDisplay = `${customer.name}${customer.first_name ? ` ${customer.first_name}` : ''} - ${customer.city || ''}`;
          const option = {
            value: customerDisplay,
            label: customerDisplay,
            data: customer,
            key: customer.id
          };
          setCustomerOptions([option]);
          setSelectedCustomer(customer);
          form.setFieldsValue({ 
            customer: customer.id,
            customerDisplay: customerDisplay
          });
        }}
      />
    </Card>
  );
};

export default LoanForm;
